import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { TransactionRule, RuleDryRunResult, RuleCommitResult, RuleUndoResult } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';
import {
  normalizeTransactionRule,
  normalizeTransactionRules,
  type NormalizedTransactionRule,
  type TransactionRuleWire,
} from '@/lib/ruleConditions';

interface TransactionRulesState {
  rules: NormalizedTransactionRule[];
  selectedRule: NormalizedTransactionRule | null;
  isLoading: boolean;

  error: string | null;
  testResult: { matched: number; sample: unknown[] } | null;
  // T155P4 — Rule Apply Preview & Undo
  dryRunResult: RuleDryRunResult | null;
  lastCommitBatchId: string | null;
  isApplying: boolean;
  isUndoing: boolean;
  undoResult: RuleUndoResult | null;
}

const initialState: TransactionRulesState = {
  rules: [],
  selectedRule: null,
  isLoading: false,
  error: null,
  testResult: null,
  dryRunResult: null,
  lastCommitBatchId: null,
  isApplying: false,
  isUndoing: false,
  undoResult: null,
};

export const fetchTransactionRules = createAsyncThunk(
  'transactionRules/fetchTransactionRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactionRules.list);
      const wire = (response.data.data || response.data) as TransactionRuleWire[];
      return normalizeTransactionRules(wire);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch rules'));
    }
  }
);

export const createTransactionRule = createAsyncThunk(
  'transactionRules/createTransactionRule',
  async (data: Partial<TransactionRule>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.create, data);
      const wire = (response.data.data || response.data) as TransactionRuleWire;
      return normalizeTransactionRule(wire);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create rule'));
    }
  }
);

export const updateTransactionRule = createAsyncThunk(
  'transactionRules/updateTransactionRule',
  async ({ id, data }: { id: string; data: Partial<TransactionRule> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.transactionRules.update(id), data);
      const wire = (response.data.data || response.data) as TransactionRuleWire;
      return normalizeTransactionRule(wire);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update rule'));
    }
  }
);

export const deleteTransactionRule = createAsyncThunk(
  'transactionRules/deleteTransactionRule',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.transactionRules.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete rule'));
    }
  }
);

export const testTransactionRule = createAsyncThunk(
  'transactionRules/testTransactionRule',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.test(id));
      return response.data.data || response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to test rule'));
    }
  }
);

export const applyAllRules = createAsyncThunk(
  'transactionRules/applyAllRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.applyAll);
      return response.data.data || response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply rules'));
    }
  }
);

// T155P4 — Preview all rules (dryRun=true) for the uncategorized bucket banner
export const previewAllRules = createAsyncThunk(
  'transactionRules/previewAllRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(`${endpoints.transactionRules.applyAll}?dryRun=true`);
      return (response.data.data || response.data) as RuleDryRunResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to preview rules'));
    }
  }
);

// T155P4 — Commit all rules (no dryRun) → returns batchId
export const commitAllRules = createAsyncThunk(
  'transactionRules/commitAllRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.applyAll);
      return (response.data.data || response.data) as RuleCommitResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply rules'));
    }
  }
);

// T155P4 — Preview a single rule (dryRun=true)
export const previewRule = createAsyncThunk(
  'transactionRules/previewRule',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`${endpoints.transactionRules.apply(id)}?dryRun=true`);
      return (response.data.data || response.data) as RuleDryRunResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to preview rule'));
    }
  }
);

// T155P4 — Commit a single rule
export const commitRule = createAsyncThunk(
  'transactionRules/commitRule',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.apply(id));
      return (response.data.data || response.data) as RuleCommitResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply rule'));
    }
  }
);

// T155P4 — Undo an automation batch by batchId
export const undoRuleBatch = createAsyncThunk(
  'transactionRules/undoRuleBatch',
  async (batchId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactionRules.undo(batchId));
      return (response.data.data || response.data) as RuleUndoResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to undo rule batch'));
    }
  }
);

export const applyRule = createAsyncThunk(
  'transactionRules/applyRule',
  async ({ id, dryRun }: { id: string; dryRun?: boolean }, { rejectWithValue }) => {
    try {
      const url = dryRun
        ? `${endpoints.transactionRules.apply(id)}?dryRun=true`
        : endpoints.transactionRules.apply(id);
      const response = await api.post(url);
      return response.data.data || response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply rule'));
    }
  }
);

const transactionRulesSlice = createSlice({
  name: 'transactionRules',
  initialState,
  reducers: {
    clearRuleError: (state) => { state.error = null; },
    clearTestResult: (state) => { state.testResult = null; },
    setSelectedRule: (state, action: PayloadAction<NormalizedTransactionRule | null>) => {
      state.selectedRule = action.payload;
    },
    // T155P4 — clear preview/undo state
    clearDryRunResult: (state) => { state.dryRunResult = null; },
    clearLastBatchId: (state) => { state.lastCommitBatchId = null; state.undoResult = null; },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchTransactionRules.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchTransactionRules.fulfilled, (state, action) => { state.isLoading = false; state.rules = action.payload; });
    builder.addCase(fetchTransactionRules.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Create
    builder.addCase(createTransactionRule.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(createTransactionRule.fulfilled, (state, action) => { state.isLoading = false; state.rules.push(action.payload); });
    builder.addCase(createTransactionRule.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Update
    builder.addCase(updateTransactionRule.fulfilled, (state, action) => {
      const idx = state.rules.findIndex(r => r._id === action.payload._id);
      if (idx !== -1) state.rules[idx] = action.payload;
    });

    // Delete
    builder.addCase(deleteTransactionRule.fulfilled, (state, action) => {
      state.rules = state.rules.filter(r => r._id !== action.payload);
    });

    // Test
    builder.addCase(testTransactionRule.pending, (state) => { state.isLoading = true; });
    builder.addCase(testTransactionRule.fulfilled, (state, action) => { state.isLoading = false; state.testResult = action.payload; });
    builder.addCase(testTransactionRule.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // Apply All (legacy)
    builder.addCase(applyAllRules.pending, (state) => { state.isLoading = true; });
    builder.addCase(applyAllRules.fulfilled, (state) => { state.isLoading = false; });
    builder.addCase(applyAllRules.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // T155P4 — Preview All Rules
    builder.addCase(previewAllRules.pending, (state) => { state.isApplying = true; state.error = null; });
    builder.addCase(previewAllRules.fulfilled, (state, action) => { state.isApplying = false; state.dryRunResult = action.payload; });
    builder.addCase(previewAllRules.rejected, (state, action) => { state.isApplying = false; state.error = action.payload as string; });

    // T155P4 — Preview Single Rule
    builder.addCase(previewRule.pending, (state) => { state.isApplying = true; state.error = null; });
    builder.addCase(previewRule.fulfilled, (state, action) => { state.isApplying = false; state.dryRunResult = action.payload; });
    builder.addCase(previewRule.rejected, (state, action) => { state.isApplying = false; state.error = action.payload as string; });

    // T155P4 — Commit All Rules
    builder.addCase(commitAllRules.pending, (state) => { state.isApplying = true; state.error = null; });
    builder.addCase(commitAllRules.fulfilled, (state, action) => {
      state.isApplying = false;
      state.dryRunResult = null;
      state.lastCommitBatchId = action.payload.batchId ?? null;
    });
    builder.addCase(commitAllRules.rejected, (state, action) => { state.isApplying = false; state.error = action.payload as string; });

    // T155P4 — Commit Single Rule
    builder.addCase(commitRule.pending, (state) => { state.isApplying = true; state.error = null; });
    builder.addCase(commitRule.fulfilled, (state, action) => {
      state.isApplying = false;
      state.dryRunResult = null;
      state.lastCommitBatchId = action.payload.batchId ?? null;
    });
    builder.addCase(commitRule.rejected, (state, action) => { state.isApplying = false; state.error = action.payload as string; });

    // T155P4 — Undo Batch
    builder.addCase(undoRuleBatch.pending, (state) => { state.isUndoing = true; state.error = null; });
    builder.addCase(undoRuleBatch.fulfilled, (state, action) => {
      state.isUndoing = false;
      state.undoResult = action.payload;
      state.lastCommitBatchId = null;
    });
    builder.addCase(undoRuleBatch.rejected, (state, action) => { state.isUndoing = false; state.error = action.payload as string; });
  },
});

export const { clearRuleError, clearTestResult, setSelectedRule, clearDryRunResult, clearLastBatchId } = transactionRulesSlice.actions;
export default transactionRulesSlice.reducer;
